import type { Folder } from '@email/shared';
import { create } from 'zustand';

export const MAIL_DRAG_MIME = 'application/x-wintermute-mail';

export interface MailDragPayload {
  accountId: number;
  threadIds: number[];
  sourceFolderId?: number;
}

interface MailDragState {
  payload: MailDragPayload | null;
  begin: (payload: MailDragPayload) => void;
  end: () => void;
}

export const useMailDragStore = create<MailDragState>((set) => ({
  payload: null,
  begin: (payload) => set({ payload }),
  end: () => set({ payload: null }),
}));

export type MailDropDecision =
  | 'allowed'
  | 'same_folder'
  | 'account_mismatch'
  | 'virtual_folder';

export function beginMailDrag(dataTransfer: DataTransfer, payload: MailDragPayload): void {
  useMailDragStore.getState().begin(payload);
  dataTransfer.effectAllowed = 'move';
  dataTransfer.setData(MAIL_DRAG_MIME, JSON.stringify(payload));
  dataTransfer.setData(
    'text/plain',
    payload.threadIds.length === 1
      ? 'Wintermute conversation'
      : `${payload.threadIds.length.toLocaleString()} Wintermute conversations`,
  );
}

export function endMailDrag(): void {
  useMailDragStore.getState().end();
}

export function readMailDrag(dataTransfer: DataTransfer): MailDragPayload | null {
  return parseMailDragPayload(dataTransfer.getData(MAIL_DRAG_MIME));
}

export function parseMailDragPayload(raw: string): MailDragPayload | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (!positiveInteger(value.accountId)) return null;
    if (
      !Array.isArray(value.threadIds) ||
      value.threadIds.length < 1 ||
      value.threadIds.length > 500 ||
      !value.threadIds.every(positiveInteger)
    ) {
      return null;
    }
    if (value.sourceFolderId !== undefined && !positiveInteger(value.sourceFolderId)) return null;
    return {
      accountId: value.accountId,
      threadIds: [...new Set(value.threadIds)],
      ...(value.sourceFolderId === undefined ? {} : { sourceFolderId: value.sourceFolderId }),
    };
  } catch {
    return null;
  }
}

export function mailDropDecision(
  payload: MailDragPayload,
  folder: Pick<Folder, 'id' | 'accountId' | 'specialUse'>,
): MailDropDecision {
  if (folder.accountId !== payload.accountId) return 'account_mismatch';
  if (folder.id === payload.sourceFolderId) return 'same_folder';
  if (folder.specialUse === 'all' || folder.specialUse === 'flagged') return 'virtual_folder';
  return 'allowed';
}

function positiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}
